import React from 'react';
import _ from 'lodash';
import {getTagFromData as getElementTag} from '@packages/utilities/platform';
import {
  isFigureMediaWrapperElement,
  isTypographicElement,
} from '@packages/utilities/elementTypeUtils';
import {Plugins, withMeta, withSystem, Atom} from '@packages/systems/wfdl';
import {
  iconName,
  qualify,
  typeName,
  pluginName,
} from '@packages/systems/wfdl-Qualified';

import {
  TBoolean,
  TList,
  TElement,
  TTypeConstructor,
  Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {defaultTextDivChildrenNodePojo} from '@packages/systems/core/constants/DomNodeDefaults';
import {getChildren} from '@packages/utilities/data-utils';
import {
  BOX_SEMANTIC_TAG_ICON_NAMES,
  type BoxSemanticTag,
} from '@packages/systems/core/constants/BlockElementTags';

const basicPlugin = Plugins.name('Basic');

const Block = Atom.createRender(
  ({attributes, classes, data, generateAttributes, innerHTML, renderChild}) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const Tag = data.tag ?? 'div';
    return (
      <Tag {...generateAttributes(attributes, classes)}>
        {innerHTML || getChildren(data).map((child) => renderChild(child))}
      </Tag>
    );
  }
);

const metaByTag: {
  [key: string]: {
    displayName: string;
  };
} = {
  div: {
    displayName: 'Div',
  },
  footer: {
    displayName: 'Footer',
  },
  header: {
    displayName: 'Header',
  },
  main: {
    displayName: 'Main',
  },
  nav: {
    displayName: 'Navigation',
  },
  aside: {
    displayName: 'Aside',
  },
  address: {
    displayName: 'Address',
  },
  article: {
    displayName: 'Article',
  },
  section: {
    displayName: 'Section',
  },
};

const config = createDevlinkAtom({
  showEmpty: true,
  icon: {
    type: 'Derived',
    getFromElement: (element, parent) => {
      const tag = getElementTag(element);

      if (tag === 'div' && isTypographicElement(element)) {
        return [basicPlugin, iconName('Text')];
      }

      if (parent && isFigureMediaWrapperElement(element, parent)) {
        return [basicPlugin, iconName('FigureMedia')];
      }

      const semanticIconName =
        BOX_SEMANTIC_TAG_ICON_NAMES[tag as BoxSemanticTag];
      if (semanticIconName) {
        return [basicPlugin, iconName(semanticIconName)];
      }

      return [basicPlugin, iconName('Div')];
    },
  },
  displayName: {
    type: 'Derived',
    defaultValue: 'Div Block',
    getFromElement(
      element: ExpressionElement,
      parent: null | undefined | Expression
    ) {
      const tag = getElementTag(element);
      const byTag = metaByTag[tag];

      if (tag === 'div' && isTypographicElement(element)) {
        return 'Text Block';
      }

      if (parent && isFigureMediaWrapperElement(element, parent)) {
        return 'Media';
      }

      if (byTag && byTag.displayName) {
        return byTag.displayName;
      }

      return _.startCase(tag);
    },
  },
  render: Block,
  fields: {
    children: withSystem(
      {
        label: 'Text',
        isBindable: true,
        defaultValue: defaultTextDivChildrenNodePojo,
      },
      TList(withMeta({multiLine: true}, TElement))
    ),
    text: withSystem({private: true}, TBoolean),
    tag: withMeta(
      {variant: 'menu'},
      withSystem(
        {
          defaultValue: 'div',
          label: 'Tag',
          private: true,
          isLocalizable: true,
        },
        TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
      )
    ),
  },
});

export default config;
